import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Users, SlidersHorizontal, FolderCog } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const apiErrorMessage = (err, fallback) => {
  if (err?.response?.status === 401) {
    return "Your session has expired. Please sign in again.";
  }
  return err?.response?.data?.detail || err?.message || fallback;
};

const handleAuthExpiry = (err) => {
  if (err?.response?.status === 401) {
    setTimeout(() => { window.location.href = "/"; }, 1500);
    return true;
  }
  return false;
};

const MEMBER_COLORS = ["#4F6F52", "#C08552", "#7B8CDE", "#B05E8F", "#66A182", "#D68C45", "#5B7DB1", "#A76571"];

const CATEGORIES = [
  "Groceries", "Laundry/Dry Cleaning", "Maid Salary", "Watchman/Security Salary",
  "Driver Salary", "Cook Salary", "Utilities - Electricity", "Utilities - Water",
  "Utilities - Gas", "Maintenance/Repairs", "Other Household Services",
  "Other Categories (Not Listed Above)"
];

export default function ConfigurationEditor({ open, onOpenChange, config, onSaved }) {
  const [tab, setTab] = useState("basic");
  const [saving, setSaving] = useState(false);
  const [formBasic, setFormBasic] = useState({
    drive_folder_name: "", sheet_id: "", sheet_tab_name: "", bill_splitting_enabled: false,
  });
  const [members, setMembers] = useState([]);
  const [rules, setRules] = useState([]);
  const [newMember, setNewMember] = useState({ name: "", color: MEMBER_COLORS[0] });
  const [newRule, setNewRule] = useState({
    scope: "vendor", match_value: "", member_ids: [], split_type: "equal", priority: 10,
  });

  useEffect(() => {
    if (!config) return;
    setFormBasic({
      drive_folder_name: config.drive_folder_name || "",
      sheet_id: config.sheet_id || "",
      sheet_tab_name: config.sheet_tab_name || "",
      bill_splitting_enabled: !!config.bill_splitting_enabled,
    });
    setMembers(config.members || []);
    setRules(config.split_rules || []);
    setTab("basic");
  }, [config]);

  if (!config) return null;

  const saveBasic = async (overrides = {}) => {
    setSaving(true);
    const payload = { ...formBasic, ...overrides };
    try {
      await axios.put(`${BACKEND_URL}/api/configs/${config.config_id}`, payload, { withCredentials: true });
      toast.success("Configuration updated");
      onSaved?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to update configuration"));
      handleAuthExpiry(err);
    } finally { setSaving(false); }
  };

  const toggleSplitting = async (v) => {
    // Optimistic UI + immediate persist so Members/Rules tab changes are meaningful
    setFormBasic(f => ({ ...f, bill_splitting_enabled: v }));
    try {
      await axios.put(
        `${BACKEND_URL}/api/configs/${config.config_id}`,
        { bill_splitting_enabled: v },
        { withCredentials: true }
      );
      toast.success(v ? "Bill splitting enabled" : "Bill splitting disabled");
      onSaved?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to update splitting"));
      // revert on error (unless it's auth — page will redirect)
      setFormBasic(f => ({ ...f, bill_splitting_enabled: !v }));
      handleAuthExpiry(err);
    }
  };

  const addMember = async () => {
    if (!newMember.name.trim()) { toast.error("Name required"); return; }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/configs/${config.config_id}/members`,
        newMember, { withCredentials: true }
      );
      setMembers([...members, res.data.member]);
      setNewMember({ name: "", color: MEMBER_COLORS[(members.length + 1) % MEMBER_COLORS.length] });
      onSaved?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to add member"));
      handleAuthExpiry(err);
    }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/configs/${config.config_id}/members/${id}`, { withCredentials: true });
      setMembers(members.filter(m => m.id !== id));
      setRules(rules.map(r => ({ ...r, member_ids: r.member_ids.filter(mid => mid !== id) })));
      onSaved?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to remove member"));
      handleAuthExpiry(err);
    }
  };

  const addRule = async () => {
    if (!newRule.match_value.trim()) { toast.error("Enter what to match"); return; }
    if (newRule.member_ids.length === 0) { toast.error("Select at least one member"); return; }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/configs/${config.config_id}/rules`,
        newRule, { withCredentials: true }
      );
      setRules([...rules, res.data.rule]);
      setNewRule({ scope: "vendor", match_value: "", member_ids: [], split_type: "equal", priority: 10 });
      onSaved?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to add rule"));
      handleAuthExpiry(err);
    }
  };

  const deleteRule = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/configs/${config.config_id}/rules/${id}`, { withCredentials: true });
      setRules(rules.filter(r => r.id !== id));
      onSaved?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to remove rule"));
      handleAuthExpiry(err);
    }
  };

  const toggleRuleMember = (memberId) => {
    setNewRule(r => ({
      ...r,
      member_ids: r.member_ids.includes(memberId)
        ? r.member_ids.filter(m => m !== memberId)
        : [...r.member_ids, memberId]
    }));
  };

  const memberNamesForRule = (rule) =>
    rule.member_ids.map(mid => members.find(m => m.id === mid)?.name).filter(Boolean).join(", ") || "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="config-editor-modal">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold text-stone-900">Edit Configuration</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-3 bg-stone-100 rounded-xl p-1">
            <TabsTrigger value="basic" className="rounded-lg" data-testid="tab-basic"><FolderCog className="w-3.5 h-3.5 mr-1.5" />Basic</TabsTrigger>
            <TabsTrigger value="members" className="rounded-lg" data-testid="tab-members" disabled={!formBasic.bill_splitting_enabled}><Users className="w-3.5 h-3.5 mr-1.5" />Members</TabsTrigger>
            <TabsTrigger value="rules" className="rounded-lg" data-testid="tab-rules" disabled={!formBasic.bill_splitting_enabled}><SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />Rules</TabsTrigger>
          </TabsList>

          {/* ---- Basic ---- */}
          <TabsContent value="basic" className="space-y-4 pt-4">
            <div>
              <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Folder Display Name</Label>
              <Input value={formBasic.drive_folder_name}
                onChange={e => setFormBasic({ ...formBasic, drive_folder_name: e.target.value })}
                className="mt-1 border-2 border-stone-200 rounded-xl" data-testid="edit-folder-name" />
            </div>

            <div className="flex items-start justify-between p-4 bg-stone-50 rounded-xl">
              <div className="pr-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-stone-800">Enable bill splitting</p>
                  {formBasic.bill_splitting_enabled && (
                    <span className="text-[9px] font-bold uppercase tracking-widest bg-sage-100 text-sage-700 px-2 py-0.5 rounded-full" data-testid="splitting-on-badge">On</span>
                  )}
                </div>
                <p className="text-xs text-stone-500 mt-0.5">Split invoice amounts across household members automatically. Best for shared/bachelor homes. Changes save instantly.</p>
              </div>
              <Switch checked={formBasic.bill_splitting_enabled}
                onCheckedChange={toggleSplitting}
                data-testid="toggle-bill-splitting" />
            </div>
          </TabsContent>

          {/* ---- Members ---- */}
          <TabsContent value="members" className="space-y-4 pt-4">
            <div className="space-y-2">
              {members.length === 0 ? (
                <p className="text-sm text-stone-400 italic">No members yet. Add your first member below.</p>
              ) : (
                members.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-3 rounded-xl border border-stone-200 bg-white" data-testid={`member-row-${m.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs" style={{ background: m.color }}>{m.name[0]?.toUpperCase()}</div>
                      <span className="text-sm font-medium text-stone-800">{m.name}</span>
                    </div>
                    <button onClick={() => deleteMember(m.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500" data-testid={`delete-member-${m.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="p-3 rounded-xl border-2 border-dashed border-stone-200 space-y-2">
              <div className="flex items-center gap-2">
                <Input value={newMember.name}
                  onChange={e => setNewMember({ ...newMember, name: e.target.value })}
                  placeholder="Member name"
                  className="border-2 border-stone-200 rounded-xl" data-testid="input-new-member-name" />
                <Button onClick={addMember} className="bg-[#4F6F52] hover:bg-[#3f5942] rounded-xl" data-testid="add-member-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="flex gap-1.5">
                {MEMBER_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setNewMember({ ...newMember, color: c })}
                    className={`w-6 h-6 rounded-full transition ${newMember.color === c ? 'ring-2 ring-offset-2 ring-stone-800' : ''}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ---- Rules ---- */}
          <TabsContent value="rules" className="space-y-4 pt-4">
            {members.length === 0 ? (
              <p className="text-sm text-stone-400 italic">Add members first, then define splitting rules.</p>
            ) : (
              <>
                <div className="space-y-2">
                  {rules.length === 0 ? (
                    <p className="text-xs text-stone-400 italic">No rules. By default, every item is split equally across all active members.</p>
                  ) : (
                    rules.map(r => (
                      <div key={r.id} className="flex items-center justify-between p-3 rounded-xl border border-stone-200 bg-white" data-testid={`rule-row-${r.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-bold uppercase bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">{r.scope}</span>
                            <span className="text-sm font-semibold text-stone-800">“{r.match_value}”</span>
                            <span className="text-xs text-stone-400">→</span>
                            <span className="text-xs text-stone-600">{memberNamesForRule(r)}</span>
                          </div>
                          <p className="text-[10px] text-stone-400 mt-0.5">Priority: {r.priority} · {r.split_type}</p>
                        </div>
                        <button onClick={() => deleteRule(r.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500" data-testid={`delete-rule-${r.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-3 rounded-xl border-2 border-dashed border-stone-200 space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <Select value={newRule.scope} onValueChange={v => setNewRule({ ...newRule, scope: v, match_value: "" })}>
                      <SelectTrigger className="border-2 border-stone-200 rounded-xl text-sm" data-testid="new-rule-scope"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vendor">Vendor</SelectItem>
                        <SelectItem value="category">Category</SelectItem>
                        <SelectItem value="keyword">Keyword</SelectItem>
                      </SelectContent>
                    </Select>
                    {newRule.scope === "category" ? (
                      <Select value={newRule.match_value} onValueChange={v => setNewRule({ ...newRule, match_value: v })}>
                        <SelectTrigger className="border-2 border-stone-200 rounded-xl text-sm col-span-2" data-testid="new-rule-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input value={newRule.match_value}
                        onChange={e => setNewRule({ ...newRule, match_value: e.target.value })}
                        placeholder={newRule.scope === "vendor" ? "e.g. Licious" : "e.g. chicken"}
                        className="col-span-2 border-2 border-stone-200 rounded-xl text-sm" data-testid="new-rule-match" />
                    )}
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Charge to</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {members.map(m => (
                        <button key={m.id} type="button" onClick={() => toggleRuleMember(m.id)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition ${newRule.member_ids.includes(m.id) ? 'text-white' : 'text-stone-500 bg-stone-100 hover:bg-stone-200'}`}
                          style={newRule.member_ids.includes(m.id) ? { background: m.color } : {}}
                          data-testid={`rule-member-toggle-${m.id}`}>
                          {m.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Priority</Label>
                      <Input type="number" value={newRule.priority}
                        onChange={e => setNewRule({ ...newRule, priority: parseInt(e.target.value) || 0 })}
                        className="mt-1 border-2 border-stone-200 rounded-xl text-sm" />
                    </div>
                    <Button onClick={addRule} className="bg-[#4F6F52] hover:bg-[#3f5942] rounded-xl" data-testid="add-rule-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Rule
                    </Button>
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Close</Button>
          {tab === "basic" && (
            <Button onClick={saveBasic} disabled={saving} className="bg-[#4F6F52] hover:bg-[#3f5942] rounded-xl" data-testid="save-basic-btn">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
